"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AltiumHierarchyPriorityNamingRule = void 0;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const AltiumNetNamingRule_1 = require("./AltiumNetNamingRule");
const AltiumSheetEntryNamingEligibility_1 = require("./AltiumSheetEntryNamingEligibility");
const altiumProjectFileTypes_1 = require("../../../parsers/project/altiumProjectFileTypes");
const AltiumNetNameCaseSelection_1 = require("./AltiumNetNameCaseSelection");
const AltiumNetNamingStrategy_1 = require("./AltiumNetNamingStrategy");
class AltiumHierarchyPriorityNamingRule extends AltiumNetNamingRule_1.AltiumNetNamingRule {
    apply(group, _hierarchy) {
        if (this.config.higherLevelNamesTakePriority !== altiumProjectFileTypes_1.NameNetsHierarchicallyMode.Yes) {
            return undefined;
        }
        const candidates = this.collectAllNamingCandidates(group);
        if (candidates.length === 0) {
            return undefined;
        }
        return this.selectBestCandidateWithHierarchyPriority(candidates, group);
    }
    collectAllNamingCandidates(group) {
        const candidates = [];
        const labelTypePriority = {
            'power-port': 3,
            'net-label': 1,
            'off-sheet-connector': 4,
            port: this.config.allowPortNetNames ? 5 : 999,
            'sheet-entry': this.config.allowSheetEntryNetNames ? 6 : 999,
        };
        for (const [instance, nets] of group.getNets()) {
            const hierarchyDepth = instance.path.length;
            for (const net of nets) {
                for (const label of net.labels) {
                    const isHierarchyLabel = label.labelType === 'port' || label.labelType === 'sheet-entry';
                    const isSyntheticHarnessHierarchyLabel = isHierarchyLabel && label.strategyMetadata?.harness?.harnessSource?.startsWith('synthetic-');
                    const isAliasedSyntheticNetLabel = label.strategyMetadata?.harness?.harnessSource === 'synthetic-net-label' &&
                        label.strategyMetadata.harness.aliasedFromRegularLabel === true;
                    const canContributeName = !isSyntheticHarnessHierarchyLabel && !isAliasedSyntheticNetLabel && label.canNameNet;
                    if (!canContributeName) {
                        continue;
                    }
                    const isNamingEligible = label.labelType === 'sheet-entry' ? (0, AltiumSheetEntryNamingEligibility_1.isAltiumSheetEntryNamingEligible)(group, label, this.config.mode, false)
                        : label.labelType === 'port' ?
                            (0, AltiumSheetEntryNamingEligibility_1.isAltiumPairedChildPortNamingEligible)(group, label, this.config.mode, false, true)
                            : true;
                    if (!isNamingEligible) {
                        continue;
                    }
                    candidates.push({
                        name: label.formattedLabel ?? label.originalLabel,
                        labelType: label.labelType,
                        hierarchyDepth,
                        priority: labelTypePriority[label.labelType] ?? 999,
                        label,
                        net,
                        instance,
                    });
                }
            }
        }
        const hasOrdinaryNetLabelCandidate = candidates.some((candidate) => this.isAuthoredNetLabel(candidate));
        const ordinaryCandidateNamesByNet = new Map();
        for (const candidate of candidates) {
            if (!this.isAuthoredNetLabel(candidate)) {
                continue;
            }
            const names = ordinaryCandidateNamesByNet.get(candidate.net) ?? new Set();
            names.add(candidate.label.originalLabel.toLowerCase());
            ordinaryCandidateNamesByNet.set(candidate.net, names);
        }
        return candidates.map((candidate) => {
            const isDemotableHarnessSynthetic = this.isHarnessSynthetic(candidate) &&
                this.hasRenamingOrdinaryCandidate(candidate, ordinaryCandidateNamesByNet) &&
                !(0, AltiumNetNamingStrategy_1.appliesChannelNetFormatting)(candidate.instance);
            return {
                ...candidate,
                powerPortRank: this.getPowerPortRank(candidate, hasOrdinaryNetLabelCandidate),
                hierarchyLabelRank: candidate.labelType === 'port' || candidate.labelType === 'sheet-entry' ? 1 : 0,
                busRangeNetLabelMemberRank: candidate.labelType === 'net-label' && candidate.label.strategyMetadata?.busRangeNetLabelMember === true ?
                    0
                    : 1,
                harnessSyntheticRank: isDemotableHarnessSynthetic ? 1 : 0,
                caseFoldedName: candidate.name.toLowerCase(),
                stableIdentity: this.getStableCandidateIdentity(candidate),
            };
        });
    }
    getPowerPortRank(candidate, hasOrdinaryNetLabelCandidate) {
        if (this.config.powerPortPriority === altiumProjectFileTypes_1.PowerPortNamesTakePriorityMode.Yes) {
            return candidate.labelType === 'power-port' ? 0 : 1;
        }
        const isDemotedByOrdinaryNetLabel = hasOrdinaryNetLabelCandidate &&
            (candidate.labelType === 'power-port' || candidate.labelType === 'off-sheet-connector');
        return isDemotedByOrdinaryNetLabel ? 1 : 0;
    }
    hasRenamingOrdinaryCandidate(candidate, ordinaryCandidateNamesByNet) {
        const ordinaryNames = ordinaryCandidateNamesByNet.get(candidate.net);
        if (!ordinaryNames) {
            return false;
        }
        const harness = candidate.label.strategyMetadata?.harness;
        const memberName = harness?.harnessMemberName?.toLowerCase();
        if (harness?.bundleNamedByCarrierLabel !== true || memberName === undefined) {
            return true;
        }
        return Array.from(ordinaryNames).some((name) => name !== memberName);
    }
    selectBestCandidateWithHierarchyPriority(candidates, group) {
        if (candidates.length === 0) {
            return 'NET_UNKNOWN';
        }
        candidates.sort((a, b) => {
            return (a.powerPortRank - b.powerPortRank ||
                a.hierarchyLabelRank - b.hierarchyLabelRank ||
                a.hierarchyDepth - b.hierarchyDepth ||
                a.priority - b.priority ||
                a.busRangeNetLabelMemberRank - b.busRangeNetLabelMemberRank ||
                a.harnessSyntheticRank - b.harnessSyntheticRank ||
                sigma_engine_shared_1.Utils.naturalStringSort(a.caseFoldedName, b.caseFoldedName) ||
                this.compareStrings(a.caseFoldedName, b.caseFoldedName) ||
                (0, AltiumNetNameCaseSelection_1.compareAltiumNetNameCaseVariants)(a.name, b.name) ||
                this.compareStrings(a.stableIdentity, b.stableIdentity));
        });
        const winner = candidates[0];
        if (winner.labelType === 'sheet-entry') {
            const channelPortName = this.resolveSheetEntryThroughChildPort(winner.label, group);
            if (channelPortName !== undefined) {
                return channelPortName;
            }
        }
        const winnerName = winner.labelType === 'net-label' ?
            (this.namingStrategy.getHierarchicalNetLabelName?.(winner.label, winner.net, winner.instance, group) ??
                winner.name)
            : winner.name;
        return this.dereferenceLabelName(winnerName);
    }
    isHarnessSynthetic(candidate) {
        return candidate.label.strategyMetadata?.harness?.harnessSource?.startsWith('synthetic-') ?? false;
    }
    isAuthoredNetLabel(candidate) {
        const metadata = candidate.label.strategyMetadata;
        return (candidate.labelType === 'net-label' &&
            !this.isHarnessSynthetic(candidate) &&
            (metadata?.busRangeMember !== true || metadata.busRangeNetLabelMember === true));
    }
    getStableCandidateIdentity(candidate) {
        const sheetEntryMetadata = candidate.label.strategyMetadata?.['sheet-entry-metadata'];
        const portMetadata = candidate.label.strategyMetadata?.['port-metadata'];
        return [
            candidate.instance.pathUniqueId,
            candidate.labelType,
            candidate.label.originalLabel,
            candidate.label.position[0],
            candidate.label.position[1],
            candidate.label.strategyMetadata?.harness?.harnessSource ?? '',
            sheetEntryMetadata?.sheetSymbolUniqueId ?? '',
            portMetadata?.pathUniqueId ?? '',
        ].join('\0');
    }
    compareStrings(a, b) {
        return (a === b ? 0
            : a < b ? -1
                : 1);
    }
    resolveSheetEntryThroughChildPort(sheetEntryLabel, group) {
        const sheetEntryMetadata = sheetEntryLabel.strategyMetadata?.['sheet-entry-metadata'];
        if (!sheetEntryMetadata) {
            return undefined;
        }
        const entryNameLower = sheetEntryMetadata.sheetEntryName.toLowerCase();
        const portNames = [];
        for (const [instance, nets] of group.getNets()) {
            if (instance.sheetSymbol?.uniqueId !== sheetEntryMetadata.sheetSymbolUniqueId) {
                continue;
            }
            for (const net of nets) {
                for (const label of net.labels) {
                    if (label.labelType !== 'port' || !label.formattedLabel) {
                        continue;
                    }
                    const portMetadata = label.strategyMetadata?.['port-metadata'];
                    if (!portMetadata ||
                        portMetadata.pathUniqueId !== instance.pathUniqueId ||
                        portMetadata.portName.toLowerCase() !== entryNameLower) {
                        continue;
                    }
                    if (sheetEntryMetadata.isRepeated &&
                        sheetEntryMetadata.channelIndex !== undefined &&
                        portMetadata.channelIndex !== sheetEntryMetadata.channelIndex) {
                        continue;
                    }
                    const derefed = this.dereferenceLabelName(label.formattedLabel);
                    if (derefed !== this.dereferenceLabelName(label.originalLabel)) {
                        portNames.push(derefed);
                    }
                }
            }
        }
        if (portNames.length === 0) {
            return undefined;
        }
        portNames.sort((a, b) => sigma_engine_shared_1.Utils.naturalStringSort(a, b));
        return portNames[0];
    }
}
exports.AltiumHierarchyPriorityNamingRule = AltiumHierarchyPriorityNamingRule;
//# sourceMappingURL=AltiumHierarchyPriorityNamingRule.js.map